import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, BookOpen, Microscope } from 'lucide-react';
import { SCIENCE_ARTICLES, SCIENCE_FACTS, SCIENCE_HERO } from '../constants/scienceContent';
import { THERMACUBE_CHECKOUT_URL } from '../constants/storeUrls';
import { THERMACUBE_PRICE_INR } from '../constants/thermaCube';

const EASE = [0.22, 1, 0.36, 1];

function formatInr(n) {
  return `\u20B9${Math.round(n).toLocaleString('en-IN')}`;
}

export function Science() {
  const reduceMotion = useReducedMotion();
  const t = (base) => (reduceMotion ? { duration: 0 } : { ...base });
  const articlesByDate = [...SCIENCE_ARTICLES].sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
  );

  return (
    <div className="min-h-screen pt-[132px] pb-24 bg-[#06080a] text-zinc-100">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(44,95,84,0.2),transparent_55%)] opacity-90" aria-hidden />

      {/* Hero */}
      <header className="relative border-b border-white/[0.06] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.22] bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=2400&q=80)',
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06080a]/70 via-[#06080a]/92 to-[#06080a]" aria-hidden />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={t({ duration: 0.85, ease: EASE })}
            className="max-w-3xl"
          >
            <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-teal-400/85 mb-5 flex items-center gap-2">
              <Microscope className="w-4 h-4 opacity-80" aria-hidden />
              {SCIENCE_HERO.eyebrow}
            </p>
            <h1 className="font-heading text-[clamp(2.25rem,5vw,3.75rem)] font-semibold tracking-[-0.03em] leading-[1.02] text-white mb-6">
              {SCIENCE_HERO.title}
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl leading-relaxed max-w-2xl mb-10">{SCIENCE_HERO.dek}</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={THERMACUBE_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-neutral-950 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-[#c5ebe2] transition-colors"
              >
                Shop ThermaCube · {formatInr(THERMACUBE_PRICE_INR)}
                <ArrowUpRight className="w-4 h-4" aria-hidden />
              </a>
              <a
                href="#articles"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-white/10 transition-colors"
              >
                <BookOpen className="w-4 h-4 opacity-80" aria-hidden />
                Read articles
              </a>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Facts */}
      <section className="relative max-w-[1200px] mx-auto px-5 sm:px-8 py-16 md:py-20 border-b border-white/[0.06]">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-10 text-center">At a glance</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {SCIENCE_FACTS.map((fact, index) => (
            <motion.article
              key={fact.label}
              initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={t({ duration: 0.7, ease: EASE, delay: reduceMotion ? 0 : index * 0.06 })}
              className="rounded-2xl border border-white/[0.08] bg-zinc-900/40 p-6 md:p-8 backdrop-blur-sm"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-teal-400/80 mb-3">{fact.label}</p>
              <p className="font-heading text-lg md:text-xl text-white mb-3 tracking-tight">{fact.stat}</p>
              <p className="text-zinc-500 text-sm leading-relaxed">{fact.detail}</p>
            </motion.article>
          ))}
        </div>
        <p className="text-zinc-600 text-xs text-center mt-10 max-w-2xl mx-auto leading-relaxed">
          Educational content only—not a substitute for professional medical advice, diagnosis, or treatment.
        </p>
      </section>

      {/* Article grid */}
      <section id="articles" className="relative max-w-[1200px] mx-auto px-5 sm:px-8 py-16 md:py-24 scroll-mt-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-teal-400/80 mb-3">Journal</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-[-0.02em] text-white">Guides &amp; articles</h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-md leading-relaxed">
            Deep dives on DOMS, cryotherapy, sleep, compression, inflammation, and more—plain language, evidence-aware, useful with or
            without hardware.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {articlesByDate.map((article, index) => (
            <motion.article
              key={article.slug}
              initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.12 }}
              transition={t({ duration: 0.75, ease: EASE, delay: reduceMotion ? 0 : index * 0.05 })}
            >
              <Link
                to={`/science/${article.slug}`}
                className="group block rounded-2xl overflow-hidden border border-white/[0.08] bg-zinc-900/30 hover:border-teal-400/25 hover:bg-zinc-900/50 transition-all duration-500 shadow-[0_24px_64px_-40px_rgba(0,0,0,0.9)]"
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={article.heroImage}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06080a] via-transparent to-transparent opacity-80" aria-hidden />
                  <span className="absolute top-4 left-4 font-mono text-[9px] uppercase tracking-[0.2em] text-white/90 bg-black/45 backdrop-blur-md px-2.5 py-1 rounded-md border border-white/10">
                    {article.category} · {article.readTime}
                  </span>
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-heading text-xl md:text-2xl text-white tracking-tight mb-3 group-hover:text-teal-200/95 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-zinc-500 text-sm leading-relaxed mb-5 line-clamp-3">{article.dek}</p>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-teal-400/90">
                    Read article
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
